import { Link } from "react-router-dom";
import { LogOut, Menu, MessageSquare, Settings } from "lucide-react";
import { SectionNav } from "./SectionNav";
import { signOut, useSession } from "../lib/auth";

interface TopBarProps {
  onMenuClick: () => void;
  onChatClick: () => void;
}

export function TopBar({ onMenuClick, onChatClick }: TopBarProps) {
  const user = useSession().data?.user;

  return (
    <header className="flex items-center justify-between border-b border-rule bg-surface px-3 sm:px-6">
      <div className="flex min-w-0 items-center gap-1.5 sm:gap-4">
        {/* Burger — mobile only. Opens the section nav + (on module pages) the
            module sidebar. Shown on every page so navigation never gets stuck
            on a full-width surface (home / previews). */}
        <button
          onClick={onMenuClick}
          className="rounded p-2 text-ink-muted hover:bg-black/5 hover:text-ink md:hidden"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
        <Link
          to="/"
          className="flex min-w-0 items-center"
          aria-label="garyseconomics — home"
        >
          <img
            src="/garyseconomics_logo.webp"
            alt="garyseconomics"
            className="block h-5 w-auto max-w-full object-contain min-[360px]:h-6 sm:h-7"
          />
        </Link>

        {/*
          Agent switcher. The portal fronts multiple AI-agent streams (YouTube
          comment pipeline, salience monitor, …). On mobile these links are
          folded into the burger drawer instead — see SectionNav / MobileNav.
        */}
        <SectionNav orientation="horizontal" className="hidden md:flex" />
      </div>

      <div className="flex shrink-0 items-center gap-1 text-sm sm:gap-3">
        <button
          onClick={onChatClick}
          className="rounded p-2 text-ink-muted hover:bg-black/5 hover:text-ink md:hidden"
          aria-label="Open chat"
        >
          <MessageSquare size={18} />
        </button>
        {/* Below sm, Settings + Sign out live in the burger drawer (SectionNav). */}
        {user && (
          <>
            <span
              className="hidden max-w-40 truncate text-ink-muted sm:inline"
              title={user.email}
            >
              {user.name}
            </span>
            {user.roles.includes("admin") && (
              <Link
                to="/settings/users"
                className="hidden rounded p-2 text-ink-muted hover:bg-black/5 hover:text-ink sm:inline-flex"
                aria-label="Settings"
                title="Settings"
              >
                <Settings size={18} />
              </Link>
            )}
            <button
              onClick={() => void signOut()}
              className="hidden rounded p-2 text-ink-muted hover:bg-black/5 hover:text-ink sm:inline-flex"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut size={18} />
            </button>
          </>
        )}
      </div>
    </header>
  );
}
